import * as React from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Crown,
  MoreHorizontal,
  Pencil,
  Plus,
  ShieldAlert,
  ShieldOff,
  UserCheck,
  UserPlus,
  UserX,
  X,
} from "lucide-react";
import { MODULES, PERMISSIONS, type Module } from "@poji/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckboxField, Input, Label, NativeSelect, Textarea } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/misc";
import { ActionDialog } from "@/components/action-dialog";
import { DataTable } from "@/components/data-table";
import { PageHeader } from "@/components/common";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ago, date } from "@/lib/format";

// ---------------------------------------------------------------- API biçimi

interface Assignment {
  assignmentId: string;
  roleId: string;
  marketId: string | null;
  marketCode: string | null;
  grantedAt: string | null;
}
interface Member {
  userId: string;
  email: string | null;
  name: string | null;
  jobTitle: string | null;
  isSuperAdmin: boolean;
  isActive: boolean;
  mfaRequired: boolean;
  invitedAt: string | null;
  invitedBy: string | null;
  deactivatedAt: string | null;
  roles: Assignment[];
}
interface Role {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  permissions: string[];
  memberCount: number;
}
interface Pending {
  id: string;
  actionType: string;
  payload: { kind: string; userId?: string; roleId?: string; roleName?: string };
  createdAt: string;
}
interface StaffData {
  members: Member[];
  roles: Role[];
  markets: { id: string; code: string; name: string }[];
  pendingApprovals: Pending[];
}

const MODULE_LABEL: Record<Module, string> = {
  cms: "İçerik",
  marketing: "Pazarlama",
  accounting: "Muhasebe",
  catalog: "Katalog",
  site: "Site",
  providers: "Hizmet verenler",
  tasks: "İşler",
  support: "Destek",
  users: "Kullanıcılar",
  settings: "Ayarlar",
  staff: "Ekip",
};
const PERM = new Map(PERMISSIONS.map((p) => [p.code as string, p]));
const sensitiveIn = (codes: string[]) => codes.filter((c) => PERM.get(c)?.sensitive);

const ROLE_STATUS = {
  granted: "Rol verildi.",
  pending_approval: "Rol hassas yetki içeriyor: ikinci bir yöneticinin onayına gönderildi.",
} as const;

const useStaff = () =>
  useQuery({ queryKey: ["admin-staff"], queryFn: () => api<StaffData>("/v1/admin/staff") });

function useRefresh() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["admin-staff"] }),
      qc.invalidateQueries({ queryKey: ["admin-approvals"] }),
    ]);
}

// ---------------------------------------------------------------- rol seçimi

function RolePicker({
  roles,
  value,
  onChange,
}: {
  roles: Role[];
  value: string[];
  onChange: (v: string[]) => void;
}) {
  return (
    <div className="grid gap-2 rounded-lg border p-3">
      {roles.map((r) => {
        const s = sensitiveIn(r.permissions);
        return (
          <CheckboxField
            key={r.id}
            checked={value.includes(r.id)}
            onChange={(e) =>
              onChange(e.target.checked ? [...value, r.id] : value.filter((x) => x !== r.id))
            }
            label={
              <span className="flex flex-wrap items-center gap-2">
                {r.name}
                {s.length > 0 && (
                  <Badge variant="warning">
                    <ShieldAlert className="size-3" /> onay gerekir
                  </Badge>
                )}
              </span>
            }
            hint={r.description ?? undefined}
          />
        );
      })}
    </div>
  );
}

function MarketSelect({
  id,
  markets,
  value,
  onChange,
}: {
  id: string;
  markets: StaffData["markets"];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="grid gap-2">
      <Label htmlFor={id}>Hangi ülkede geçerli</Label>
      <NativeSelect id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Tüm ülkeler</option>
        {markets.map((m) => (
          <option key={m.id} value={m.id}>
            Yalnızca {m.name}
          </option>
        ))}
      </NativeSelect>
    </div>
  );
}

// ---------------------------------------------------------------- yönetici ekle

function InviteDialog({ data }: { data: StaffData }) {
  const refresh = useRefresh();
  const [email, setEmail] = React.useState("");
  const [jobTitle, setJobTitle] = React.useState("");
  const [roles, setRoles] = React.useState<string[]>([]);
  const [market, setMarket] = React.useState("");
  const valid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
  return (
    <ActionDialog
      wide
      onOpen={() => {
        setEmail("");
        setJobTitle("");
        setRoles([]);
        setMarket("");
      }}
      trigger={
        <Button size="sm">
          <UserPlus /> Yönetici ekle
        </Button>
      }
      title="Yönetici ekle"
      description="Kişinin Poji hesabı yoksa şifre belirleme bağlantısı içeren bir davet e-postası gider. İlk girişte iki adımlı doğrulamayı kurması istenir."
      confirmLabel="Ekle"
      canSubmit={valid}
      success={(r) => {
        const x = r as { invited: boolean; roles: { status: string }[] };
        const pend = x.roles.filter((y) => y.status === "pending_approval").length;
        return `${x.invited ? "Davet e-postası gönderildi." : "Yönetici eklendi."}${pend ? ` ${pend} rol onay bekliyor.` : ""}`;
      }}
      onSubmit={async (key) => {
        const r = await api<{ invited: boolean; roles: { status: string }[] }>("/v1/admin/staff", {
          method: "POST",
          idempotencyKey: key,
          body: {
            email: email.trim(),
            jobTitle: jobTitle.trim() || null,
            roles: roles.map((roleId) => ({ roleId, marketId: market || null })),
          },
        });
        await refresh();
        return r;
      }}
    >
      <div className="grid gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="inv-email">E-posta</Label>
            <Input
              id="inv-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="inv-title">Görevi (isteğe bağlı)</Label>
            <Input
              id="inv-title"
              maxLength={80}
              placeholder="ör. Muhasebeci"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
            />
          </div>
        </div>
        <div className="grid gap-2">
          <Label>Roller</Label>
          <RolePicker roles={data.roles} value={roles} onChange={setRoles} />
          <p className="text-xs text-muted-foreground">
            “Onay gerekir” işaretli roller ikinci bir yöneticinin onayından sonra verilir (K-095).
          </p>
        </div>
        <MarketSelect id="inv-market" markets={data.markets} value={market} onChange={setMarket} />
      </div>
    </ActionDialog>
  );
}

// ---------------------------------------------------------------- kişi işlemleri

function GrantRoleDialog({ data, member }: { data: StaffData; member: Member }) {
  const refresh = useRefresh();
  const [roleId, setRoleId] = React.useState("");
  const [market, setMarket] = React.useState("");
  const role = data.roles.find((r) => r.id === roleId);
  const sens = role ? sensitiveIn(role.permissions) : [];
  return (
    <ActionDialog
      onOpen={() => {
        setRoleId("");
        setMarket("");
      }}
      trigger={
        <Button size="sm" variant="outline">
          <Plus /> Rol ver
        </Button>
      }
      title={`Rol ver: ${member.name ?? member.email}`}
      confirmLabel={sens.length ? "Onaya gönder" : "Rol ver"}
      canSubmit={Boolean(roleId)}
      success={(r) => ROLE_STATUS[(r as { status: keyof typeof ROLE_STATUS }).status]}
      onSubmit={async (key) => {
        const r = await api<{ status: keyof typeof ROLE_STATUS }>(
          `/v1/admin/staff/${member.userId}/roles`,
          { method: "POST", idempotencyKey: key, body: { roleId, marketId: market || null } },
        );
        await refresh();
        return r;
      }}
    >
      <div className="grid gap-4">
        <div className="grid gap-2">
          <Label htmlFor="grant-role">Rol</Label>
          <NativeSelect id="grant-role" value={roleId} onChange={(e) => setRoleId(e.target.value)}>
            <option value="">Seçin</option>
            {data.roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </NativeSelect>
        </div>
        <MarketSelect
          id="grant-market"
          markets={data.markets}
          value={market}
          onChange={setMarket}
        />
        {sens.length > 0 && (
          <p className="rounded-md bg-warning/15 px-3 py-2 text-sm">
            Bu rol hassas yetki içeriyor ({sens.map((s) => PERM.get(s)?.description).join(", ")}).
            İkinci bir yönetici onaylayınca verilir.
          </p>
        )}
      </div>
    </ActionDialog>
  );
}

function MemberActions({ member, isMe }: { member: Member; isMe: boolean }) {
  const refresh = useRefresh();
  const [action, setAction] = React.useState<
    null | "deactivate" | "activate" | "super" | "unsuper"
  >(null);
  const who = member.name ?? member.email ?? "kişi";
  const post = (path: string) => async (key: string) => {
    await api(`/v1/admin/staff/${member.userId}/${path}`, {
      method: "POST",
      idempotencyKey: key,
      body: {},
    });
    await refresh();
  };
  if (isMe) return <span className="text-xs text-muted-foreground">siz</span>;
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="icon" variant="ghost" aria-label={`${who} işlemleri`}>
            <MoreHorizontal />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {member.isActive ? (
            <DropdownMenuItem onSelect={() => setAction("deactivate")}>
              <UserX />
              Pasifleştir
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onSelect={() => setAction("activate")}>
              <UserCheck />
              Yeniden etkinleştir
            </DropdownMenuItem>
          )}
          {member.isActive &&
            (member.isSuperAdmin ? (
              <DropdownMenuItem onSelect={() => setAction("unsuper")}>
                <ShieldOff />
                Super admin yetkisini al
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onSelect={() => setAction("super")}>
                <Crown />
                Super admin yap (onayla)
              </DropdownMenuItem>
            ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <ActionDialog
        open={action === "deactivate"}
        onOpenChange={(o) => !o && setAction(null)}
        title={`${who}: pasifleştir`}
        description="Panele giremez; en geç 1 dakika içinde tüm yetkileri durur. Hesap ve kayıtları silinmez, rolleri korunur; yeniden etkinleştirilebilir."
        confirmLabel="Pasifleştir"
        confirmVariant="destructive"
        onSubmit={post("deactivate")}
        success="Pasifleştirildi."
      />
      <ActionDialog
        open={action === "activate"}
        onOpenChange={(o) => !o && setAction(null)}
        title={`${who}: yeniden etkinleştir`}
        description="Önceki rolleriyle panele yeniden girebilir."
        confirmLabel="Etkinleştir"
        onSubmit={post("activate")}
        success="Etkinleştirildi."
      />
      <ActionDialog
        open={action === "super"}
        onOpenChange={(o) => !o && setAction(null)}
        title={`${who}: super admin yap`}
        description="Super admin her şeye yetkilidir. İstek ikinci bir yöneticinin onayına gider (K-095); onaylanınca uygulanır."
        confirmLabel="Onaya gönder"
        onSubmit={post("super-admin")}
        success="Onaya gönderildi."
      />
      <ActionDialog
        open={action === "unsuper"}
        onOpenChange={(o) => !o && setAction(null)}
        title={`${who}: super admin yetkisini al`}
        description="Hemen uygulanır; kişi yalnızca rollerindeki yetkilerle kalır. Son super admin olamaz."
        confirmLabel="Yetkiyi al"
        confirmVariant="destructive"
        onSubmit={post("super-admin/revoke")}
        success="Super admin yetkisi alındı."
      />
    </>
  );
}

function RevokeRole({
  member,
  assignment,
  role,
}: {
  member: Member;
  assignment: Assignment;
  role: Role | undefined;
}) {
  const refresh = useRefresh();
  return (
    <ActionDialog
      trigger={
        <button
          type="button"
          className="rounded-sm opacity-60 hover:opacity-100"
          aria-label={`${role?.name ?? "rol"} rolünü al`}
        >
          <X className="size-3" />
        </button>
      }
      title="Rolü geri al"
      description={`${member.name ?? member.email} kişisinden “${role?.name ?? "?"}” rolü alınacak. Hemen uygulanır (en geç 1 dakika).`}
      confirmLabel="Geri al"
      confirmVariant="destructive"
      success="Rol geri alındı."
      onSubmit={async (key) => {
        await api(`/v1/admin/staff/${member.userId}/roles/${assignment.assignmentId}/revoke`, {
          method: "POST",
          idempotencyKey: key,
          body: {},
        });
        await refresh();
      }}
    />
  );
}

// ---------------------------------------------------------------- rol düzenle

function RoleDialog({ role }: { role?: Role }) {
  const refresh = useRefresh();
  const [name, setName] = React.useState("");
  const [code, setCode] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [perms, setPerms] = React.useState<string[]>([]);
  const reset = () => {
    setName(role?.name ?? "");
    setCode(role?.code ?? "");
    setDescription(role?.description ?? "");
    setPerms(role?.permissions ?? []);
  };
  const added = perms.filter((p) => !role?.permissions.includes(p));
  const newSensitive = sensitiveIn(added);
  const autoCode = name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/^[^a-z]+/, "")
    .slice(0, 46);
  const toggle = (c: string, on: boolean) =>
    setPerms((x) => (on ? [...new Set([...x, c])] : x.filter((y) => y !== c)));

  return (
    <ActionDialog
      wide
      onOpen={reset}
      trigger={
        role ? (
          <Button size="sm" variant="outline">
            <Pencil /> Düzenle
          </Button>
        ) : (
          <Button size="sm" variant="outline">
            <Plus /> Yeni rol
          </Button>
        )
      }
      title={role ? `Rol: ${role.name}` : "Yeni rol"}
      description={
        role
          ? `${role.memberCount} kişide bu rol var; değişiklik hepsine en geç 1 dakikada yansır.${role.isSystem ? " Hazır rol: silinemez, yetkileri değiştirilebilir." : ""}`
          : "Ör. “Stajyer yazar”. Hassas yetkiler ikinci bir yöneticinin onayıyla eklenir."
      }
      confirmLabel={newSensitive.length ? "Kaydet ve onaya gönder" : "Kaydet"}
      canSubmit={Boolean(name.trim() && (role || (code || autoCode).length >= 2))}
      success={(r) =>
        (r as { pendingApprovalId?: string | null } | undefined)?.pendingApprovalId
          ? "Kaydedildi; hassas yetkiler onaya gönderildi."
          : "Rol kaydedildi."
      }
      onSubmit={async (key) => {
        let result: unknown;
        if (role) {
          if (name.trim() !== role.name || (description.trim() || null) !== role.description)
            await api(`/v1/admin/staff/roles/${role.id}`, {
              method: "PATCH",
              body: { name: name.trim(), description: description.trim() || null },
            });
          result = await api(`/v1/admin/staff/roles/${role.id}/permissions`, {
            method: "PUT",
            idempotencyKey: key,
            body: { permissions: perms },
          });
        } else
          result = await api("/v1/admin/staff/roles", {
            method: "POST",
            idempotencyKey: key,
            body: {
              code: code || autoCode,
              name: name.trim(),
              description: description.trim() || null,
              permissions: perms,
            },
          });
        await refresh();
        return result;
      }}
    >
      <div className="grid gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="role-name">Ad</Label>
            <Input
              id="role-name"
              maxLength={80}
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          {!role && (
            <div className="grid gap-2">
              <Label htmlFor="role-code">Sistem kodu</Label>
              <Input
                id="role-code"
                value={code}
                placeholder={autoCode || "ör. intern_writer"}
                pattern="[a-z][a-z0-9_]{1,46}"
                onChange={(e) => setCode(e.target.value.toLowerCase())}
              />
            </div>
          )}
          <div className="grid gap-2 sm:col-span-2">
            <Label htmlFor="role-desc">Açıklama</Label>
            <Textarea
              id="role-desc"
              className="min-h-14"
              maxLength={255}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>
        <div className="grid gap-3">
          {MODULES.map((m) => {
            const list = PERMISSIONS.filter((p) => p.module === m);
            const on = list.filter((p) => perms.includes(p.code)).length;
            return (
              <fieldset key={m} className="rounded-lg border p-3">
                <legend className="flex items-center gap-2 px-1 text-sm font-medium">
                  {MODULE_LABEL[m]}
                  <span className="text-xs font-normal text-muted-foreground">
                    {on}/{list.length}
                  </span>
                </legend>
                <div className="grid gap-2 sm:grid-cols-2">
                  {list.map((p) => (
                    <CheckboxField
                      key={p.code}
                      checked={perms.includes(p.code)}
                      onChange={(e) => toggle(p.code, e.target.checked)}
                      label={
                        <span className="flex flex-wrap items-center gap-1.5">
                          {p.description}
                          {p.sensitive && (
                            <Badge variant="warning" title="Hassas: eklemek dört göz onayı ister">
                              <ShieldAlert className="size-3" /> hassas
                            </Badge>
                          )}
                        </span>
                      }
                      hint={p.code}
                    />
                  ))}
                </div>
              </fieldset>
            );
          })}
        </div>
        {newSensitive.length > 0 && (
          <p className="rounded-md bg-warning/15 px-3 py-2 text-sm">
            Hassas yetkiler ({newSensitive.map((s) => PERM.get(s)?.description).join(", ")}) ikinci
            bir yönetici onaylayınca eklenir; diğer değişiklikler hemen uygulanır.
          </p>
        )}
      </div>
    </ActionDialog>
  );
}

// ---------------------------------------------------------------- sayfa

export function StaffPage() {
  const q = useStaff();
  const { state } = useAuth();
  const me = state.kind === "ready" ? state.staff.userId : null;
  const data = q.data;
  const roleBy = React.useMemo(() => new Map((data?.roles ?? []).map((r) => [r.id, r])), [data]);
  const pendingFor = (userId: string) =>
    (data?.pendingApprovals ?? []).filter((p) => p.payload.userId === userId);

  const memberColumns: ColumnDef<Member, unknown>[] = [
    {
      id: "who",
      header: "Kişi",
      accessorFn: (m) => m.email,
      cell: ({ row }) => {
        const m = row.original;
        return (
          <div className="min-w-48">
            <div className="flex items-center gap-2 font-medium">
              {m.name ?? m.email}
              {m.userId === me && <Badge variant="outline">siz</Badge>}
            </div>
            <div className="text-xs text-muted-foreground">
              {m.name && m.email}
              {m.jobTitle && `${m.name ? " · " : ""}${m.jobTitle}`}
            </div>
          </div>
        );
      },
    },
    {
      id: "roles",
      header: "Roller",
      cell: ({ row }) => {
        const m = row.original;
        const pend = pendingFor(m.userId);
        return (
          <div className="flex max-w-md flex-wrap gap-1">
            {m.isSuperAdmin && (
              <Badge>
                <Crown className="size-3" /> Super admin
              </Badge>
            )}
            {m.roles.map((a) => {
              const r = roleBy.get(a.roleId);
              return (
                <Badge key={a.assignmentId} variant="secondary">
                  {r?.name ?? "?"}
                  {a.marketCode && ` · ${a.marketCode}`}
                  {m.userId !== me && <RevokeRole member={m} assignment={a} role={r} />}
                </Badge>
              );
            })}
            {pend.map((p) => (
              <Badge key={p.id} variant="warning" title="İkinci yöneticinin onayını bekliyor">
                {p.payload.kind === "super_admin" ? "Super admin" : p.payload.roleName} · onay
                bekliyor
              </Badge>
            ))}
            {!m.isSuperAdmin && !m.roles.length && !pend.length && (
              <span className="text-xs text-muted-foreground">Rol yok (hiçbir ekranı göremez)</span>
            )}
          </div>
        );
      },
    },
    {
      id: "status",
      header: "Durum",
      cell: ({ row }) =>
        row.original.isActive ? (
          <Badge variant="success">Aktif</Badge>
        ) : (
          <Badge variant="secondary" title={date(row.original.deactivatedAt)}>
            Pasif
          </Badge>
        ),
    },
    {
      id: "invited",
      header: "Eklendi",
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {row.original.invitedAt ? ago(row.original.invitedAt) : "—"}
          {row.original.invitedBy && (
            <span className="block text-xs">{row.original.invitedBy}</span>
          )}
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) =>
        data && (
          <div className="flex items-center justify-end gap-1">
            {row.original.isActive && row.original.userId !== me && (
              <GrantRoleDialog data={data} member={row.original} />
            )}
            <MemberActions member={row.original} isMe={row.original.userId === me} />
          </div>
        ),
    },
  ];

  const roleColumns: ColumnDef<Role, unknown>[] = [
    {
      id: "name",
      header: "Rol",
      accessorFn: (r) => r.name,
      cell: ({ row }) => (
        <div className="min-w-48">
          <div className="flex items-center gap-2 font-medium">
            {row.original.name}
            {row.original.isSystem && <Badge variant="outline">hazır</Badge>}
          </div>
          <div className="text-xs text-muted-foreground">{row.original.description}</div>
        </div>
      ),
    },
    {
      id: "perms",
      header: "Yetkiler",
      cell: ({ row }) => {
        const s = sensitiveIn(row.original.permissions);
        return (
          <span className="text-sm">
            {row.original.permissions.length} yetki
            {s.length > 0 && (
              <Badge variant="warning" className="ml-2">
                <ShieldAlert className="size-3" /> {s.length} hassas
              </Badge>
            )}
          </span>
        );
      },
    },
    { accessorKey: "memberCount", header: "Kişi" },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <div className="flex justify-end">
          <RoleDialog role={row.original} />
        </div>
      ),
    },
  ];

  const pendingCount = data?.pendingApprovals.length ?? 0;
  return (
    <>
      <PageHeader
        title="Ekip ve roller"
        description="Panele kimin girebileceği ve neyi görebileceği. Her değişiklik denetim kaydına yazılır; kimse silinmez, pasifleştirilir."
        actions={data && <InviteDialog data={data} />}
      />
      {pendingCount > 0 && (
        <p className="mb-4 rounded-lg border border-warning/40 bg-warning/10 px-4 py-3 text-sm">
          {pendingCount} ekip isteği ikinci yöneticinin onayını bekliyor.{" "}
          <Link to="/approvals" className="font-medium text-primary hover:underline">
            Dört göz onayı ekranı
          </Link>
          . Kendi isteğinizi onaylayamazsınız.
        </p>
      )}
      <Tabs defaultValue="members">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <TabsList>
            <TabsTrigger value="members">Ekip ({data?.members.length ?? "…"})</TabsTrigger>
            <TabsTrigger value="roles">Roller ({data?.roles.length ?? "…"})</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="members">
          <DataTable
            columns={memberColumns}
            data={data?.members}
            isLoading={q.isPending}
            error={q.error}
            onRetry={() => void q.refetch()}
            empty="Ekipte kimse yok."
          />
        </TabsContent>
        <TabsContent value="roles">
          <div className="mb-3 flex justify-end">
            <RoleDialog />
          </div>
          <DataTable
            columns={roleColumns}
            data={data?.roles}
            isLoading={q.isPending}
            error={q.error}
            onRetry={() => void q.refetch()}
            empty="Rol yok."
          />
        </TabsContent>
      </Tabs>
    </>
  );
}
